import { Component, HostListener, Injector, NgZone, Renderer2, Signal, ViewChild } from '@angular/core';
import { AlertBannerComponent } from 'alert-banner-lib';
import { PendoService } from 'analytics-lib';
import { AwsRumService } from 'analytics-lib';
import { GoogleAnalyticsService } from 'analytics-lib';
import { EntityControllerService } from 'entity-picker-lib';
import { Namespace } from 'enums-lib';
import { FeatureFlagService } from 'feature-flags-lib';
import { ErrorSeverity, MessageAction, MessageNotificationService } from 'message-notification-service-lib';
import { PostLoginActionService, TemplateLaunchIntentService } from 'post-login-action-service-lib';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { SharedServiceProviderServiceFactory } from 'shared-service-provider-lib';
import { IdentityStorage } from 'storage-service-lib';
import { environment, runtimeEnvironment } from '../../environments/environment';
import { ChromeUrlMonitor } from '../chrome-url-monitor';
import { MergeComponentConfig } from '../merge-component/merge.component.config';
import { RuntimeEnvironmentService } from '../services/runtime-environment.service';
import { ServiceProviderService } from '../services/service.provider.service';
import { TemplateLaunchOverlayService } from '../workflow-in-progress/template-launch-overlay.service';

@Component({
  selector: 'app-document-client',
  templateUrl: './document-client.component.html',
  styleUrls: ['../chrome-extension.app.component.scss', '../app.component.scss'],
  standalone: false,
})
export class DocumentClientComponent {
  originNamespace = '';
  version: string;
  expandVersion = false;

  runningAsChromeExtension;
  renderMode = 'web-app';
  styles = {};
  ready = false;
  frameCollapsed = false;
  showSpinner = false;
  load: { icon: string; text: string } = {
    icon: 'app-loading',
    text: 'Loading AwesomeDocs',
  };
  popupReminder: string;

  filesListOpen = true; // On load only the file list open
  templatesListOpen = false;

  mergeComponentConfig: MergeComponentConfig;

  protected readonly senderPickerFeatureEnabled: Signal<boolean> = this.featureFlagService.enabled(
    FeatureFlagService.FLAGS.createTemplateAsDifferentUser
  );

  @ViewChild('alertbanner')
  alertBanner: AlertBannerComponent;

  private webAppStyles = {};

  private chromeExtensionStyles = {
    width: '100%',
    height: '100%',
    color: '#FFFFFF',
  };

  constructor(
    private messageNotificationService: MessageNotificationService,
    private serviceProviderService: ServiceProviderService,
    private sharedServiceProviderServiceFactory: SharedServiceProviderServiceFactory,
    private awsRumService: AwsRumService,
    private gaService: GoogleAnalyticsService,
    private postLoginActionService: PostLoginActionService,
    private runtimeEnvironmentService: RuntimeEnvironmentService,
    private pendoService: PendoService,
    private ngZone: NgZone,
    private injector: Injector,
    private renderer: Renderer2,
    private identityStorage: IdentityStorage,
    private featureFlagService: FeatureFlagService,
    private templateLaunchIntentService: TemplateLaunchIntentService,
    private templateLaunchOverlayService: TemplateLaunchOverlayService,
  ) {
    this.version = environment.version;

    this.messageNotificationService.message$.subscribe((message) => {
      console.log(`KPAD ${environment.version}`, message);
      ngZone.run(() => {
        this.showSpinner = false;
      });
      setTimeout(() => {
        if (message.action === MessageAction.SHOW) {
          this.alertBanner.show(message.message);
        } else {
          this.alertBanner.hide(message.message);
        }
      }, 0); // hack cos alertBanner is undefined until AfterViewInit
    });

    this.initialiseApp();
  }

  // Keyboard Shortcuts
  // - Use Alt + Shift + V to enlarge version number
  @HostListener('document:keydown', ['$event'])
  protected handleKeydownEvent(event: KeyboardEvent) {
    if (event.altKey && event.shiftKey) {
      if (event.code === 'KeyV') {
        this.expandVersion = true;
        setTimeout(() => {
          window.scrollTo(0, document.body.scrollHeight);
        }, 200);
        setTimeout(() => {
          this.expandVersion = false;
        }, 5000);
      }
    }
  }

  copyToClipboard(msg: any): void {
    const listener = (e: ClipboardEvent) => {
      e.clipboardData.setData('text/plain', msg);
      e.preventDefault();
      document.removeEventListener('copy', listener);
    };
    document.addEventListener('copy', listener);
    document.execCommand('copy');
  }

  toggleExtension(): void {
    this.frameCollapsed = !this.frameCollapsed;
    chrome.runtime.sendMessage({
      message: 'toggle_frame',
      collapse: this.frameCollapsed,
    });
  }

  closeExtension(): void {
    chrome.runtime.sendMessage({ message: 'close_frame' });
  }

  onFilesListExpand(isOpened: boolean) {
    this.filesListOpen = isOpened;
    this.templatesListOpen = false;
  }

  onTemplatesListExpand(isOpened: boolean) {
    this.templatesListOpen = isOpened;
    this.filesListOpen = false;
  }

  private initialiseApp(): void {
    this.replaceConsoleDebug();
    this.updateLoadStatus('app-loading', 'Loading AwesomeDocs');
    try {
      this.runtimeEnvironmentService.initialise().subscribe(
        (_) => {
          this.runtimeEnvironmentReady();

          const signInTimeoutId = setTimeout(() => {
            this.updateLoadStatus('app-loading', 'Sign-in is taking a little while. Please wait.');
            this.popupReminder = 'Sign-in is taking longer than expected. Please check for a sign-in window.';
          }, 20000);

          this.triggerLogin()
            .pipe(finalize(() => clearTimeout(signInTimeoutId)))
            .subscribe(
              (__) => this.loginSuccess(),
              (error) => this.runtimeEnvironmentError(error)
            );
        },
        (error) => this.runtimeEnvironmentError(error)
      );
    } catch (error) {
      console.error(`KPAD ${environment.version} error during load`, error);
      this.updateLoadStatus('error', error.message ? error.message : 'An error occurred.');
    }
  }

  private initialiseAnalytics(): void {
    this.awsRumService.init();
    this.gaService.init();
    if (!runtimeEnvironment.runningAsChromeExtension) {
      this.pendoService.initPendoScript(environment.pendoWebAppAPIKey);
    }
  }

  private runtimeEnvironmentReady(): void {
    console.log('runtime', runtimeEnvironment);

    this.runningAsChromeExtension = runtimeEnvironment.runningAsChromeExtension;
    this.renderMode = runtimeEnvironment.renderMode;
    this.originNamespace = runtimeEnvironment.origin.namespace;

    if (!this.runningAsChromeExtension) {
      this.styles = this.webAppStyles;
      this.renderer.addClass(document.body, 'web-app');
    } else {
      this.styles = this.chromeExtensionStyles;
      this.renderer.addClass(document.body, 'chrome-extension');
    }
  }

  private runtimeEnvironmentError(error: any): void {
    if (error.status) {
      if (error.status === 404) {
        const clientName = runtimeEnvironment.client.name ?? '';
        this.updateLoadStatus(
          'broken_image',
          `The client ${clientName} was not found or is not enabled. ` + `If this is correct please contact Kyloe Support.`
        );
      } else {
        this.updateLoadStatus(
          'error',
          `An error (${error.status}) occurred loading the application. ` +
            'Try waiting a few minutes and refreshing the page. ' +
            'If this keeps happening please contact Kyloe Support.'
        );
      }
    } else {
      let msg = 'Sign-in failed.';
      if (typeof error === 'string') {
        msg += error;
      }
      this.updateLoadStatus('error', msg);
    }
    console.error('KPAD error getting client info.', error);
  }

  private updateLoadStatus(icon: string, text: string): void {
    this.ngZone.run(() => {
      this.load = { icon, text };
    });
  }

  private replaceConsoleDebug(): void {
    const debugConsole = console.debug;
    console.debug = (message?: any, ...optionalParams: any[]) => {
      if (!environment.production) {
        debugConsole(`%c${message}`, 'font-style:italic;', '', ...optionalParams);
      }
      // debugConsole(`%cKP-DEBUG%c ${message}`, 'background:#f9f9f9;color:#ce0058;font-weight:bold;font-size:8px;', '', ...optionalParams);
    };
  }

  private triggerLogin(): Observable<void> {
    const authService = this.serviceProviderService.getAuthService(runtimeEnvironment.origin.namespace);
    const existingIdentity = this.identityStorage.get();
    const existingToken = existingIdentity?.token ?? null;
    const existingTenant = existingIdentity?.tenant ?? null;
    this.identityStorage.put({
      namespace: Namespace[runtimeEnvironment.origin.namespace],
      tenant: runtimeEnvironment.client.name ?? existingTenant,
      token: existingToken,
    });
    return authService.getToken().pipe(
      tap((jwt) => (runtimeEnvironment.jwt = jwt)),
      map((_) => null),
      catchError((error) => {
        this.identityStorage.clear();
        return throwError(() => error);
      })
    );
  }

  private loginSuccess(): void {
    if (runtimeEnvironment.runningAsChromeExtension) {
      ChromeUrlMonitor.start(this.injector.get(EntityControllerService));
    }

    this.mergeComponentConfig = new MergeComponentConfig(
      runtimeEnvironment.origin.namespace,
      this.serviceProviderService,
      this.sharedServiceProviderServiceFactory
    );

    const serviceProvider = this.sharedServiceProviderServiceFactory.getServiceProviderService(this.identityStorage.get()?.namespace);
    serviceProvider
      .getEntityMetadataService()
      .getEntitiesMetadata()
      .subscribe((_) => {
        this.ready = true;
        setTimeout(() => {
          this.postLoginActionService.execute(runtimeEnvironment.origin.namespace);
          this.beginTemplateLaunchWithFlag();
        }, 0);
      });
    this.initialiseAnalytics(); // TODO: Once identityStorage is actually being put() on login, analytics called there would be cleaner
  }

  // to be replaced by beginTemplateLaunch on flag removal
  private beginTemplateLaunchWithFlag(): void {
    this.featureFlagService.ready().subscribe(() => {
      if (!this.featureFlagService.enabled(FeatureFlagService.FLAGS.templateDirectUrl)()) {
        return;
      }

      this.beginTemplateLaunch();
    });
  }

  private beginTemplateLaunch(): void {
    if (!this.templateLaunchIntentService.isPending()) {
      return;
    }
    const hasRecord = !!this.injector.get(EntityControllerService).entity;

    if (!hasRecord) {
      this.templateLaunchIntentService.consume();
      this.messageNotificationService.error(
        'This AwesomeDoc direct template url is missing a record reference, please contact Kyloe Support',
        undefined,
        undefined,
        ErrorSeverity.CRITICAL,
      );
      return;
    }

    this.templateLaunchOverlayService.show();
  }
}
